import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { usePublicOrder } from "../context/PublicOrderContext";
import { useEffect, useRef, useState } from "react";
import api from "../utils/api";
import CustomerNavbar from "./CustomerNavbar";
import { Helmet } from "react-helmet-async";
import { toast } from "react-toastify";

const LandingPage = () => {
  const navigate = useNavigate();
  const { slug } = useParams();
  const { setOrderInfo, setOrderId, setRestaurant, restaurant } = usePublicOrder();
  const [searchParams] = useSearchParams();

  const floorName = searchParams.get("floorName");
  const sectionName = searchParams.get("sectionName");
  const unitNumber = searchParams.get("unitNumber");
  const sectionType = searchParams.get("sectionType");
  const roomType = searchParams.get("roomType");

  const [storeName, setStoreName] = useState("");
  const [storeLogo, setStoreLogo] = useState("");
  const [isLocking, setIsLocking] = useState(false);
  const [lockError, setLockError] = useState("");
  const [pendingOrderId, setPendingOrderId] = useState(null);
  const [pendingOrderDetails, setPendingOrderDetails] = useState(null);

  // 🔹 Fetch restaurant basic info
  useEffect(() => {
    const fetchRestaurant = async () => {
      try {
        const res = await api.get(`/api/public/restaurant/${slug}`);
        setRestaurant(res.data);
        setStoreName(res.data.name);
        setStoreLogo(res.data.logo);
      } catch (err) {
        console.error("Failed to fetch restaurant", err);
      }
    };

    fetchRestaurant();

    const storedOrderId = localStorage.getItem(`orderId_${slug}`);
    if (storedOrderId) {
      setPendingOrderId(storedOrderId);
      
      const fetchOrderDetails = async () => {
        try {
          const res = await api.get(`/api/public/restaurant/${slug}/order/${storedOrderId}`);
          if (res.data.status === "CLOSED" || res.data.status === "CANCELLED") {
            localStorage.removeItem(`orderId_${slug}`);
            sessionStorage.removeItem("table_lock_active");
            setPendingOrderId(null);
          } else {
            setPendingOrderDetails(res.data);
          }
        } catch (err) {
          console.error("Failed to fetch pending order details", err);
        }
      };

      fetchOrderDetails();
    }
  }, [slug, setRestaurant]);

  // 🔹 Automatic Table QR Order Locking
  // `restaurant` changes twice on load (localStorage copy, then the fresh fetch);
  // lock each table only once so we don't fire two get-or-create calls
  const lockedTableRef = useRef(null);
  useEffect(() => {
    const lockTableOrder = async () => {
      if (!slug || !floorName || !sectionName || !unitNumber || !sectionType) {
        return;
      }

      if (restaurant && restaurant.orderCapabilities?.dineInEnabled === false) {
         setLockError("Dine-In ordering is currently disabled for this restaurant. You can only place Takeaway orders.");
         return;
      }

      const tableKey = [slug, floorName, sectionName, unitNumber, sectionType, roomType].join("|");
      if (lockedTableRef.current === tableKey) return;
      lockedTableRef.current = tableKey;

      setIsLocking(true);
      setLockError("");

      try {
        console.log(`[QR Lock] Resolving table parameters: Floor=${floorName}, Section=${sectionName}, Table/Room=${unitNumber}`);

        const payload = {
          floorName,
          sectionName,
          unitNumber,
          sectionType,
          roomType: roomType || "NA",
        };

        const res = await api.post(`/api/public/restaurant/${slug}/order/table/get-or-create`, payload);
        const order = res.data;
        
        setOrderInfo(order);
        setOrderId(order._id);
        localStorage.setItem(`orderId_${slug}`, order._id);
        
        sessionStorage.setItem("table_lock_active", "true");
        sessionStorage.setItem("table_lock_order", order._id);

        setTimeout(() => {
          setIsLocking(false);
          // MenuPage reads the order from the URL — without it "Confirm Order" does nothing
          navigate(`/r/${slug}/menu?orderId=${order._id}`);
        }, 1200);
      } catch (err) {
        console.error("Failed to resolve or lock table order", err);
        setLockError(err?.response?.data?.message || "Failed to secure connection to this table. Please ask staff for assistance.");
        setIsLocking(false);
      }
    };

    if (restaurant) {
      lockTableOrder();
    }
  }, [slug, floorName, sectionName, unitNumber, sectionType, roomType, restaurant, navigate, setOrderId, setOrderInfo]);

  // 🔹 Parcel Order
  const handleParcel = async () => {
    try {
      const res = await api.post(`/api/public/restaurant/${slug}/order`, { orderType: "DIRECT" });
      const newOrder = res.data;

      setOrderInfo(newOrder);
      setOrderId(newOrder._id);
      localStorage.setItem(`orderId_${slug}`, newOrder._id);
      sessionStorage.removeItem("table_lock_active");

      navigate(`/r/${slug}/menu?orderId=${newOrder._id}`);
    } catch (err) {
      console.error("Failed to create parcel order", err);
      toast.error(err?.response?.data?.message || "Could not start a takeaway order. Please try again.");
    }
  };

  if (isLocking) {
    return (
      <div className="min-h-screen bg-slate-900 flex flex-col items-center justify-center text-white px-6">
        <Helmet>
          <title>Connecting to Table... | {storeName || 'Cloud Billing'}</title>
        </Helmet>
        <div className="relative flex items-center justify-center mb-8">
          <div className="animate-ping absolute inline-flex h-20 w-20 rounded-full bg-teal-400 opacity-20"></div>
          <div className="relative rounded-full p-6 bg-teal-500/10 border border-teal-500/30">
            <span className="text-4xl">🔒</span>
          </div>
        </div>
        <h2 className="text-2xl font-bold text-center mb-2 animate-pulse">
          Securing Table Connection
        </h2>
        <p className="text-slate-400 text-center text-sm max-w-xs">
          Locking your session to {sectionType === "TABLE" ? "Table" : "Room"} {unitNumber} ({floorName}). Please wait...
        </p>
      </div>
    );
  }

  if (lockError) {
    return (
      <div className="min-h-screen bg-slate-900 flex flex-col items-center justify-center text-white px-6">
        <Helmet>
          <title>Connection Failed | {storeName || 'Cloud Billing'}</title>
        </Helmet>
        <div className="rounded-full p-6 bg-red-500/10 border border-red-500/30 mb-6">
          <span className="text-4xl">⚠️</span>
        </div>
        <h2 className="text-2xl font-bold text-center mb-2 text-red-400">
          Lock Connection Failed
        </h2>
        <p className="text-slate-400 text-center text-sm max-w-sm mb-8">
          {lockError}
        </p>
        <button
          onClick={() => window.location.reload()}
          className="bg-teal-600 hover:bg-teal-700 text-white px-6 py-2.5 rounded-full font-bold shadow-lg hover:shadow-teal-500/20 transition-all duration-300 transform active:scale-95"
        >
          Retry Connection
        </button>
      </div>
    );
  }


  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>{storeName ? `${storeName} - Digital Menu & Ordering` : 'Cloud Billing Restaurant'}</title>
        <meta name="description" content={`Explore the digital menu and place an order online at ${storeName || 'our restaurant'}!`} />
        
        {/* Open Graph / WhatsApp Properties */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={window.location.href} />
        <meta property="og:title" content={`${storeName || 'Restaurant'} - Digital Menu`} />
        <meta property="og:description" content={`Check out the menu and order your favorite dishes online from ${storeName || 'our restaurant'}.`} />
        {storeLogo && <meta property="og:image" content={storeLogo} />}
        {storeLogo && <meta property="og:image:secure_url" content={storeLogo} />}
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={window.location.href} />
        <meta name="twitter:title" content={`${storeName || 'Restaurant'} - Digital Menu`} />
        <meta name="twitter:description" content={`Check out the menu and order your favorite dishes online from ${storeName || 'our restaurant'}.`} />
        {storeLogo && <meta name="twitter:image" content={storeLogo} />}
      </Helmet>

      {/* Navbar */}
      <CustomerNavbar
        storeName={storeName}
        storeLogo={storeLogo}
        slug={slug}
      />

      {/* Content */}
      <div className="pt-24 sm:pt-28 px-4 sm:px-6 flex flex-col items-center">

        <h1 className="text-3xl font-bold text-gray-800 mb-4">
          Welcome to {storeName}
        </h1>

        <p className="text-gray-500 mb-10 text-center max-w-md">
          Please choose how you'd like to place your order.
        </p>

        {pendingOrderId && (
          <div className="w-full max-w-3xl mb-8 bg-gradient-to-r from-teal-50 to-indigo-50 border border-teal-100 rounded-2xl p-5 shadow-sm flex flex-col sm:flex-row items-center justify-between gap-4 animate-scale-up">
            <div className="flex items-center gap-3 text-center sm:text-left">
              <div className="p-3 bg-teal-100/60 rounded-xl text-teal-600 flex items-center justify-center">
                <span className="text-2xl">⏳</span>
              </div>
              <div>
                <div className="flex flex-wrap items-center gap-2 justify-center sm:justify-start">
                  <h3 className="font-extrabold text-teal-950 text-sm">Active Ordering Session</h3>
                  {pendingOrderDetails && (
                    <span className="px-2 py-0.5 text-[10px] font-black uppercase bg-slate-900 text-white rounded-full">
                      #{pendingOrderDetails.orderNumber}
                    </span>
                  )}
                  {pendingOrderDetails && (
                    <span className={`px-2 py-0.5 text-[10px] font-black uppercase rounded-full ${
                      pendingOrderDetails.status === "OPEN" ? "bg-blue-100 text-blue-700" :
                      pendingOrderDetails.status === "ORDER_PLACED" ? "bg-indigo-100 text-indigo-700 animate-pulse" :
                      pendingOrderDetails.status === "PREPARING" ? "bg-orange-100 text-orange-700" :
                      pendingOrderDetails.status === "READY" ? "bg-teal-100 text-teal-700 animate-bounce" :
                      "bg-gray-100 text-gray-700"
                    }`}>
                      {pendingOrderDetails.status === "ORDER_PLACED" ? "Placed" : pendingOrderDetails.status}
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-500 font-semibold mt-1">
                  {pendingOrderDetails ? (
                    <>
                      {pendingOrderDetails.orderType === "DINE_IN" ? (
                        `Dine-In: Floor ${pendingOrderDetails.floorName}, ${pendingOrderDetails.sectionType === "TABLE" ? "Table" : "Room"} ${pendingOrderDetails.unitNumber}`
                      ) : (
                        "Takeaway / Parcel"
                      )}
                      {" • "}
                      <span className="font-bold text-indigo-650">₹{pendingOrderDetails.totalAmount}</span>
                      {` (${pendingOrderDetails.items?.length || 0} items)`}
                    </>
                  ) : (
                    "Retrieving order status..."
                  )}
                </p>
              </div>
            </div>
            <div className="flex gap-2 w-full sm:w-auto">
              <button
                onClick={() => navigate(`/r/${slug}/menu?orderId=${pendingOrderId}`)}
                className="flex-1 sm:flex-none py-2.5 px-5 bg-teal-600 hover:bg-teal-700 text-white font-extrabold text-xs rounded-xl shadow-sm transition whitespace-nowrap uppercase tracking-wider"
              >
                Resume Order
              </button>
              <button
                onClick={() => {
                  localStorage.removeItem(`orderId_${slug}`);
                  sessionStorage.removeItem("table_lock_active");
                  setPendingOrderId(null);
                  setPendingOrderDetails(null);
                }}
                className="py-2 px-3 border border-gray-250 hover:bg-slate-50 text-gray-400 hover:text-gray-600 rounded-xl transition text-xs font-bold"
                title="Clear Session"
              >
                Clear
              </button>
            </div>
          </div>
        )}

        <div className={`grid grid-cols-1 md:grid-cols-${(restaurant?.orderCapabilities?.dineInEnabled !== false && restaurant?.orderCapabilities?.takeawayEnabled !== false) ? 2 : 1} gap-8 w-full max-w-3xl`}>

          {restaurant?.orderCapabilities?.dineInEnabled !== false && (
            <div
              onClick={() => {
                navigate(`/r/${slug}/tables`);
              }}
              className="cursor-pointer bg-white shadow-lg rounded-2xl p-8 text-center hover:shadow-xl transition transform hover:-translate-y-1"
            >
              <div className="text-5xl mb-4">🍽</div>
              <h2 className="text-xl font-semibold text-gray-800 mb-2">
                Dine In
              </h2>
              <p className="text-gray-500">
                Select your table and enjoy your meal inside.
              </p>
            </div>
          )}

          {restaurant?.orderCapabilities?.takeawayEnabled !== false && (
            <div
              onClick={handleParcel}
              className="cursor-pointer bg-white shadow-lg rounded-2xl p-8 text-center hover:shadow-xl transition transform hover:-translate-y-1"
            >
              <div className="text-5xl mb-4">🛍</div>
              <h2 className="text-xl font-semibold text-gray-800 mb-2">
                Takeaway
              </h2>
              <p className="text-gray-500">
                Order for self pickup or parcel.
              </p>
            </div>
          )}

          {restaurant?.orderCapabilities?.dineInEnabled === false && restaurant?.orderCapabilities?.takeawayEnabled === false && (
            <div className="col-span-full bg-slate-100 rounded-2xl p-8 text-center border border-slate-200">
               <div className="text-5xl mb-4 opacity-50">🚫</div>
               <h2 className="text-xl font-bold text-slate-700 mb-2">Currently Unavailable</h2>
               <p className="text-slate-500">
                  This restaurant is currently not accepting digital orders.
               </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LandingPage;
